import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Grid, Paper, Typography } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import moment from 'moment';

import { StatsGrouping, useUserReachStatsQuery } from 'api';
import { Charting, LoaderComponents, Loaders, Numeric, TextTransform } from 'components';
import { useCurrentUser, useNotify, useProtectedClient } from 'hooks';
import { getReachByProvider, getTotalReach, IReachByProvider } from 'helpers';

import { PIE_CHART_HEIGHT } from '../../constants';
import Value from './Value';

const useStyles = makeStyles(theme => ({
  legendIcon: {
    height: 16,
    width: 16,
    borderRadius: '50%',
  },
  emptyStateWrapper: {
    position: 'relative',
    height: PIE_CHART_HEIGHT,
  },
  value: {
    height: 25,
  },
  center: theme.mixins.absoluteCenter,
}));

const MyFollowers: FC = () => {
  const classes = useStyles();
  const currentUser = useCurrentUser();
  const client = useProtectedClient();
  const notify = useNotify();
  const theme = useTheme();
  const { t } = useTranslation();

  const [variables, setVariables] = useState({
    grouping: StatsGrouping.Year,
    value: moment().year(),
  });

  const { data, isLoading } = useUserReachStatsQuery(
    {
      user_id: currentUser.id,
      grouping: variables.grouping,
      value: variables.value,
    },
    {
      onError: notify.queryError,
    }
  );

  const reachByProvider = getReachByProvider(data?.userReachStats);

  function getColor(provider: string) {
    return theme.custom.networks[provider];
  }

  function getLegendIcon(entry: IReachByProvider) {
    const backgroundColor = getColor(entry.provider);

    return <div style={{ backgroundColor }} className={classes.legendIcon} />;
  }

  function getLegendLabel(entry: IReachByProvider) {
    return entry.provider === 'Company' ? (
      client.title
    ) : (
      <TextTransform transform="capitalize">{entry.provider}</TextTransform>
    );
  }

  function renderLegend() {
    return (
      <Grid container direction="column" spacing={3}>
        {reachByProvider.map(entry => (
          <Grid item key={entry.provider}>
            <Grid container alignItems="center" justify="space-between">
              <Grid item>
                <Grid container spacing={1} alignItems="center">
                  <Grid item>{getLegendIcon(entry)}</Grid>
                  <Grid item>{getLegendLabel(entry)}</Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Typography variant="subtitle2" className={classes.value}>
                  {isLoading ? (
                    <LoaderComponents.Block width={25} height={25} />
                  ) : (
                    <Numeric value={entry.reach} />
                  )}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        ))}
      </Grid>
    );
  }

  function renderEmptyState() {
    return (
      <Fragment>
        <div className={classes.emptyStateWrapper}>
          <Loaders.PieChart size={PIE_CHART_HEIGHT} chartSize={160} />
          <Typography align="center" className={classes.center} variant="subtitle2">
            {t('emptyStates:activityMyReach')}
          </Typography>
        </div>
      </Fragment>
    );
  }

  function renderChart() {
    if (isLoading) {
      return <Loaders.PieChart size={PIE_CHART_HEIGHT} chartSize={160} />;
    }

    if (!isLoading && !getTotalReach(reachByProvider)) {
      return renderEmptyState();
    }

    return (
      <ResponsiveContainer height={PIE_CHART_HEIGHT}>
        <PieChart data={reachByProvider}>
          <Pie
            data={reachByProvider}
            dataKey="reach"
            nameKey="provider"
            innerRadius={60}
            outerRadius={80}
            paddingAngle={2}
            cy="40%"
          >
            {reachByProvider.map((datum: IReachByProvider) => (
              <Cell key={datum.provider} fill={getColor(datum.provider)} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    );
  }

  return (
    <Paper>
      <Box paddingLeft={3} paddingTop={3} marginBottom={4}>
        <Typography variant="h6" paragraph>
          {t('components:myFollowers')}
        </Typography>
        <Value isLoading={isLoading} value={getTotalReach(reachByProvider)} />
      </Box>
      {renderChart()}
      <Box paddingLeft={3} paddingRight={3} paddingBottom={2}>
        {renderLegend()}
      </Box>
      <Divider />
      <Box paddingLeft={1}>
        <Charting.PeriodSelect variables={variables} onChange={changed => setVariables(changed)} />
      </Box>
    </Paper>
  );
};

export default MyFollowers;
